import type { FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

const RECAPTCHA_ENTERPRISE_SITE_KEY =
  process.env.EXPO_PUBLIC_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_KEY;

// Keep this list aligned with the domains on the production Enterprise key.
// localhost and ephemeral preview domains intentionally do not attest.
const APP_CHECK_HOSTS = new Set([
  "neqraw.com",
  "schoolme-e31ec.web.app",
  "schoolme-e31ec.firebaseapp.com",
  "neqraw.expo.app",
  "neqraw--staging.expo.app",
]);

type FirebaseAppCheckRuntime = typeof globalThis & {
  __neqrawAppCheckInstances?: WeakMap<FirebaseApp, AppCheck>;
};

export function initializeFirebaseAppCheck(app: FirebaseApp): AppCheck | null {
  // Expo may evaluate Web modules while exporting static HTML in Node.
  if (typeof window === "undefined" || !APP_CHECK_HOSTS.has(window.location.hostname)) {
    return null;
  }

  if (!RECAPTCHA_ENTERPRISE_SITE_KEY) {
    console.warn(
      "Missing EXPO_PUBLIC_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_KEY for an App Check Web origin."
    );
    return null;
  }

  const runtime = globalThis as FirebaseAppCheckRuntime;
  const instances = runtime.__neqrawAppCheckInstances ??= new WeakMap<FirebaseApp, AppCheck>();
  const existing = instances.get(app);
  if (existing) return existing;

  const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
  instances.set(app, appCheck);
  return appCheck;
}
